import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ClueCard from "@/components/base/ClueCard";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { clues } from "@/mocks/clues";

function CategoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((item) => item.slug === slug);
  const categoryClues = useMemo(
    () => clues.filter((item) => item.categorySlug === slug),
    [slug]
  );

  const jsonLd = useMemo(() => {
    if (!category) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${category.name} - 爆料平台主题分类`,
        description: category.description,
        url: `https://gregbennettguitars.com.cn/category/${category.slug}`,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://gregbennettguitars.com.cn/" },
          { "@type": "ListItem", position: 2, name: "主题分类", item: "https://gregbennettguitars.com.cn/categories" },
          {
            "@type": "ListItem",
            position: 3,
            name: category.name,
            item: `https://gregbennettguitars.com.cn/category/${category.slug}`,
          },
        ],
      },
    ];
  }, [category]);

  useSeo({
    title: category ? `${category.name} - 爆料线索分类归档 | 爆料平台` : "分类不存在 | 爆料平台",
    description: category ? category.description : "该分类可能已被调整，请返回主题分类总览查看。",
    keywords: category ? `${category.name},爆料平台,匿名爆料,线索归档` : "爆料平台",
    path: `/category/${slug ?? ""}`,
    jsonLd,
  });

  if (!category) {
    return (
      <div className="flex min-h-[50vh] w-full flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-folder-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
        <p className="mt-2 text-sm text-foreground-600">该分类可能已被调整，你可以回到分类总览继续浏览。</p>
        <Link
          to="/categories"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回主题分类
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "主题分类", to: "/categories" },
              { label: category.name },
            ]}
          />
          <div className="mt-5 flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
                {category.name}
              </h1>
              <p className="mt-1 text-xs font-label uppercase tracking-widest text-foreground-400">
                {category.enName} · 已归档 {category.count} 条
              </p>
            </div>
          </div>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {category.description}
          </p>
        </div>
      </div>

      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          该分类下的公开线索
        </h2>
        {categoryClues.length > 0 ? (
          <div className="mt-6 space-y-4">
            {categoryClues.map((clue) => (
              <ClueCard key={clue.slug} clue={clue} variant="horizontal" />
            ))}
          </div>
        ) : (
          <p className="mt-6 rounded-lg border border-dashed border-background-300 py-12 text-center text-sm text-foreground-500">
            该分类暂无公开线索，欢迎通过匿名入口提交。
          </p>
        )}

        <div className="mt-12 flex flex-col items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              手里有「{category.name}」相关的材料？
            </h2>
            <p className="mt-1 text-sm text-foreground-600">匿名提交，不必暴露身份，我们会按分类归档。</p>
          </div>
          <Link
            to="/submit"
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-send-plane-fill" />
            匿名爆料
          </Link>
        </div>
      </div>
    </div>
  );
}

export default CategoryDetailPage;